import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getPartnerShopReviews,
  type PartnerShopReviewPageData,
} from '@/api/partnerShopDetail';

const hideDescription = false;

export default function ReviewsPage() {
  const navigate = useNavigate();
  const params = useParams<{
    shopSlug?: string;
    slug?: string;
  }>();

  const shopSlug = params.shopSlug || params.slug || '';

  const [pageData, setPageData] = useState<PartnerShopReviewPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeFilter, setActiveFilter] = useState('전체');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let ignore = false;

    async function loadReviews() {
      if (!shopSlug) {
        setErrorMessage('업체 주소가 올바르지 않습니다.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage('');

      try {
        const result = await getPartnerShopReviews(shopSlug, {
          preview: true,
        });

        if (!ignore) {
          setPageData(result);
          setActiveFilter('전체');
        }
      } catch (error) {
        console.error('[ReviewsPage] failed to load partner shop reviews', error);

        if (!ignore) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : '후기 정보를 불러오지 못했습니다.'
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadReviews();

    return () => {
      ignore = true;
    };
  }, [shopSlug]);

  const filteredReviews = useMemo(() => {
    const reviews = pageData?.reviews || [];

    if (activeFilter === '전체') {
      return reviews;
    }

    return reviews.filter((review) => review.source === activeFilter);
  }, [activeFilter, pageData]);

  const positiveMax = pageData?.positiveBars?.[0]?.count || 1;
  const cautionMax = pageData?.cautionBars?.[0]?.count || 1;

  const goBack = () => navigate(-1);

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-[#F7F2EA] flex items-center justify-center">
        <p className="text-[13px] text-[#536079]">후기를 불러오는 중입니다.</p>
      </div>
    );
  }

  if (errorMessage || !pageData) {
    return (
      <div className="min-h-[100dvh] bg-[#F7F2EA] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-5 border border-[#E8E2DA] text-center">
          <p className="text-[14px] font-semibold text-[#121B35]">
            후기 정보를 불러오지 못했습니다.
          </p>
          <p className="text-[12px] text-[#536079] mt-2">
            {errorMessage || '잠시 후 다시 시도해 주세요.'}
          </p>
          <button
            onClick={goBack}
            className="mt-4 px-4 py-2 rounded-lg bg-[#121B35] text-white text-[12px] font-medium"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  const {
    shop,
    overview,
    summary,
    filters,
    positiveBars,
    cautionBars,
    checkTips,
  } = pageData;

  return (
    <div className="min-h-[100dvh] bg-[#F7F2EA]">
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#E8E2DA]">
        <div className="flex items-center justify-between h-14 px-4">
          <button
            onClick={goBack}
            className="flex items-center justify-center w-8 h-8 -ml-1"
          >
            <i className="ri-arrow-left-s-line text-xl text-[#121B35]" />
          </button>

          <h1 className="text-[15px] font-semibold text-[#121B35] truncate max-w-[70%]">
            {shop.name} 후기
          </h1>

          <button className="flex items-center justify-center w-8 h-8">
            <i className="ri-share-line text-lg text-[#121B35]" />
          </button>
        </div>
      </div>

      <div className="hidden lg:block sticky top-0 z-40 bg-white border-b border-[#E8E2DA]">
        <div className="max-w-[1200px] mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <button
              onClick={goBack}
              className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[#F7F2EA] transition-colors"
            >
              <i className="ri-close-line text-xl text-[#121B35]" />
            </button>

            <h1 className="text-[17px] font-semibold text-[#121B35]">
              {shop.name} 후기
            </h1>
          </div>

          <button className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[#F7F2EA] transition-colors">
            <i className="ri-share-line text-lg text-[#121B35]" />
          </button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-4 lg:py-6">
        <p className="text-[13px] font-semibold text-[#121B35] mb-3">
          다이렉트 후기 {overview.total.toLocaleString()}건을 분석했어요.
        </p>

        <div className="lg:flex lg:gap-8 lg:items-start">
          <div className="lg:flex-1 lg:max-w-[800px]">
            <div
              className="bg-white rounded-2xl p-4 lg:p-5 border border-[#E8E2DA] mb-4"
              style={{ display: 'none' }}
            >
              <div
                className="grid grid-cols-2 lg:grid-cols-4 gap-3"
                style={{ display: 'none' }}
              >
                <div className="bg-[#F7F2EA] rounded-xl p-3">
                  <p className="text-[11px] text-[#536079]">구체 평가 후기</p>
                  <p className="text-[18px] font-bold text-[#121B35] mt-1">
                    {overview.evaluated.toLocaleString()}개
                  </p>
                </div>

                <div className="bg-[#F7F2EA] rounded-xl p-3">
                  <p className="text-[11px] text-[#536079]">직접 평가</p>
                  <p className="text-[18px] font-bold text-[#121B35] mt-1">
                    {overview.direct.toLocaleString()}개
                  </p>
                </div>

                <div className="bg-[#F7F2EA] rounded-xl p-3">
                  <p className="text-[11px] text-[#536079]">간접 평가</p>
                  <p className="text-[18px] font-bold text-[#121B35] mt-1">
                    {overview.indirect.toLocaleString()}개
                  </p>
                </div>

                <div className="bg-[#F7F2EA] rounded-xl p-3">
                  <p className="text-[11px] text-[#536079]">관련 언급</p>
                  <p className="text-[18px] font-bold text-[#121B35] mt-1">
                    {overview.mentionedOnly.toLocaleString()}개
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 lg:p-5 border border-[#E8E2DA] mb-4">
              <p className="text-[11px] tracking-[0.16em] text-[#B9965B] mb-2">
                AI REVIEW SUMMARY
              </p>

              <p
                className="font-['MaruBuri'] text-[15px] lg:text-[17px] text-[#121B35] leading-[1.9] tracking-[-0.02em]"
                style={{ fontWeight: 600, color: 'black' }}
              >
                “{summary.quote || '아직 확정된 후기 요약이 없습니다.'}”
              </p>
            </div>

            <div className="mt-4 lg:grid lg:grid-cols-2 lg:gap-4" style={{display: 'none'}}>
              <div className="bg-white rounded-2xl p-4 md:p-5">
                <h3 className="text-sm md:text-base font-semibold text-[#121B35] mb-3">
                  좋았던 점
                </h3>

                <div className="space-y-4">
                  {summary.pros.length === 0 && (
                    <p className="text-xs md:text-sm text-[#536079]">
                      아직 정리된 장점이 없습니다.
                    </p>
                  )}

                  {summary.pros.map((item, index) => (
                    <div key={`${item.title}-${index}`} className="flex gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#B9965B] shrink-0 mt-2" />

                      <div>
                        <p className="text-sm md:text-base text-[#121B35] font-medium leading-relaxed">
                          {item.title}
                        </p>

                        {hideDescription ? null : (
                          <p className="text-xs md:text-sm text-[#536079] mt-1 leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-4 md:p-5 mt-3 lg:mt-0">
                <h3 className="text-sm md:text-base font-semibold text-[#121B35] mb-3">
                  아쉬운 점
                </h3>

                <div className="space-y-4">
                  {summary.notes.length === 0 && (
                    <p className="text-xs md:text-sm text-[#536079]">
                      아직 정리된 확인 포인트가 없습니다.
                    </p>
                  )}

                  {summary.notes.map((item, index) => (
                    <div key={`${item.title}-${index}`} className="flex gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#B9965B] shrink-0 mt-2" />

                      <div>
                        <p className="text-sm md:text-base text-[#121B35] font-medium leading-relaxed">
                          {item.title}
                        </p>

                        {hideDescription ? null : (
                          <p className="text-xs md:text-sm text-[#536079] mt-1 leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div
              className="bg-white rounded-2xl p-4 lg:p-5 border border-[#E8E2DA] mb-4"
              style={{ display: 'none' }}
            >
              <h3 className="text-[13px] font-semibold text-[#121B35] mb-2">
                방문 전 체크하면 좋은 점
              </h3>

              <div className="space-y-2">
                {checkTips.map((item) => (
                  <div
                    key={item}
                    className="flex gap-2 text-[12px] text-[#536079] leading-[1.6]"
                  >
                    <span className="text-[#B9965B] shrink-0">·</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="sticky top-14 lg:top-16 z-30 bg-[#F7F2EA] py-3 -mx-4 px-4 lg:-mx-6 lg:px-6 lg:mb-4">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                {filters.map((filter) => (
                  <button
                    key={filter.label}
                    onClick={() => setActiveFilter(filter.label)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors ${
                      activeFilter === filter.label
                        ? 'bg-[#121B35] text-white'
                        : 'bg-white text-[#536079] border border-[#E8E2DA]'
                    }`}
                  >
                    {filter.label}
                    <span
                      className={`ml-1 ${
                        activeFilter === filter.label ? 'text-[#B9965B]' : 'text-[#9AA3B2]'
                      }`}
                    >
                      {filter.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3 lg:space-y-0 gap-3 lg:grid lg:grid-cols-2">
              {filteredReviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-white rounded-2xl p-4 lg:p-5 border border-[#E8E2DA]"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-[14px] lg:text-[15px] font-semibold text-[#121B35] leading-[1.5]">
                      {review.title}
                    </h3>

                    <span className="text-[11px] text-[#9AA3B2] shrink-0">
                      {review.date}
                    </span>
                  </div>

                  <p className="text-[11px] text-[#B9965B] mb-2">
                    {review.source}
                  </p>

                  <p className="text-[13px] text-[#121B35] leading-[1.7] mb-3">
                    {review.summary}
                  </p>

                  {review.photoThumbnails.length > 0 && (
                    <div className="flex gap-2 mb-3 overflow-x-auto scrollbar-hide">
                      {review.photoThumbnails.map((src, index) => (
                        <div
                          key={`${src}-${index}`}
                          className="shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-[#E8E2DA]"
                        >
                          <img
                            src={src}
                            alt=""
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {review.contentPreview && (
                    <div className="bg-[#F7F2EA] rounded-xl p-3 mb-3">
                      <p className="text-[11px] font-medium text-[#536079] mb-1">
                        원문 일부
                      </p>

                      <p className="text-[12px] text-[#536079] leading-[1.7] line-clamp-4">
                        {review.contentPreview}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {review.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[11px] text-[#536079] bg-[#F7F2EA] px-2 py-0.5 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {review.goodPoints.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[11px] font-medium text-[#121B35] mb-1">
                        좋았던 점
                      </p>

                      <div className="flex flex-wrap gap-1.5">
                        {review.goodPoints.map((point) => (
                          <span
                            key={point}
                            className="text-[11px] text-[#B9965B] bg-[#F7F2EA] px-2 py-0.5 rounded-full"
                          >
                            {point}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {review.checkPoints.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[11px] font-medium text-[#121B35] mb-1">
                        확인할 점
                      </p>

                      <div className="flex flex-wrap gap-1.5">
                        {review.checkPoints.map((point) => (
                          <span
                            key={point}
                            className="text-[11px] text-[#536079] bg-white border border-[#E8E2DA] px-2 py-0.5 rounded-full"
                          >
                            {point}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {review.originalUrl && (
                    <a
                      href={review.originalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[12px] text-[#121B35] font-medium underline underline-offset-2"
                    >
                      네이버 카페 원문 보기
                      <i className="ri-external-link-line text-xs" />
                    </a>
                  )}
                </div>
              ))}

              {filteredReviews.length === 0 && (
                <div className="text-center py-16 text-[13px] text-[#536079]">
                  이 필터에 해당하는 후기가 없습니다.
                </div>
              )}
            </div>
          </div>

          <div className="hidden lg:block lg:w-[320px] lg:shrink-0">
            <div className="sticky top-[80px] space-y-4">
              <div className="bg-white rounded-2xl p-5 border border-[#E8E2DA]">
                <h3 className="text-[13px] font-semibold text-[#121B35] mb-3">
                  후기 요약
                </h3>

                <p className="text-[12px] text-[#536079] leading-[1.7] mb-3">
                  {summary.quote}
                </p>

                <div className="space-y-2">
                  {positiveBars.slice(0, 3).map((bar) => (
                    <div
                      key={bar.label}
                      className="flex items-center justify-between text-[12px]"
                    >
                      <span className="text-[#121B35]">{bar.label}</span>
                      <span className="text-[#536079] tabular-nums">
                        {bar.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {cautionBars.length > 0 && (
                <div className="bg-white rounded-2xl p-5 border border-[#E8E2DA]">
                  <h3 className="text-[13px] font-semibold text-[#121B35] mb-2">
                    확인하면 좋은 점
                  </h3>

                  <div className="space-y-2">
                    {cautionBars.slice(0, 5).map((bar) => (
                      <div
                        key={bar.label}
                        className="flex gap-2 text-[12px] text-[#536079] leading-[1.6]"
                      >
                        <span className="text-[#B9965B] shrink-0">·</span>
                        <span>{bar.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl p-5 border border-[#E8E2DA]">
                <p className="text-[13px] font-semibold text-[#121B35] mb-3">
                  {shop.name}
                </p>

                <button className="w-full bg-[#121B35] text-white rounded-xl py-3 text-[13px] font-medium whitespace-nowrap mb-2">
                  상담 문의하기
                </button>

                <button className="w-full border border-[#121B35] text-[#121B35] rounded-xl py-3 text-[13px] font-medium whitespace-nowrap">
                  예약하기
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}